<template>
  <div ref="root" class="relative">
    <gp-btn size="sm" :label="buttonLabel" :icon="faGlobe" @click="open = !open" />
    <div
      v-if="open"
      class="bg-gp-midnight-1 z-15 absolute bottom-full right-0 mb-2 max-h-[60vh] min-w-48 overflow-y-auto rounded py-1 drop-shadow-2xl"
    >
      <button
        v-for="option in options"
        :key="option.value ?? 'auto'"
        type="button"
        class="hover:bg-gp-midnight-2 flex w-full items-center gap-2 whitespace-nowrap px-3 py-1.5 text-left text-sm"
        :class="option.value === localeOverride ? 'text-gp-cyan' : 'text-gp-rhino hover:text-white'"
        @click="select(option.value)"
      >
        <font-awesome-icon
          :icon="faCheck"
          class="w-3 shrink-0"
          :class="{ invisible: option.value !== localeOverride }"
        />
        <span>{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faCheck, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { GpBtn } from "@gportal/uikit";
import { onClickOutside } from "@vueuse/core";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import { SUPPORTED_LOCALES, ensureLocale, localeOverride, setLocaleOverride } from "../../i18n";
import { useServiceStore } from "../../store/service";
import { getRouteLocale, useRouteUtils } from "../../util/route";

interface LocaleOption {
  // `null` means "no override, follow the route/website locale"
  value: string | null;
  label: string;
}

const service = useServiceStore();
const route = useRoute();
const { locale } = useRouteUtils();

const open = ref(false);
const root = ref<HTMLElement>();
onClickOutside(root, () => (open.value = false));

/** Native language name for a locale code, e.g. "de" -> "Deutsch". */
const languageName = (code: string): string => {
  try {
    return new Intl.DisplayNames([code], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
};

const options = computed<Array<LocaleOption>>(() => [
  { value: null, label: `Auto (${getRouteLocale(route)})` },
  ...SUPPORTED_LOCALES.map((code) => ({ value: code, label: `${languageName(code)} (${code})` })).sort((a, b) =>
    a.label.localeCompare(b.label),
  ),
]);

const buttonLabel = computed(() => `lang: ${locale.value}${localeOverride.value ? "" : " (auto)"}`);

const select = async (value: string | null) => {
  open.value = false;
  setLocaleOverride(value);
  // Reload messages for the newly selected locale, including the game specific ones
  const extraGameTranslationKeys = (service.ctx.sys?.extraGameTranslationKeys ?? []).filter(
    (key): key is string => !!key,
  );
  await ensureLocale(locale.value, service.ctx.sys?.game?.key, extraGameTranslationKeys);
};
</script>
